<template>
  <div class="py-2 my-2 help-container">
    <h1 class="separator py-4">WORKING DRAFT</h1>
    <span class="banner py-3 mb-4">
      <div>This page will provide an overview of each GL Group, GL account, and your Calculation & Assumption information. This page will be formatted with subtotals similar to a Profit & Loss statement.</div>
      <div>Expand any of the topics below for more information on the Working Draft.</div>
    </span>

    <!--Help Sections-->
    <v-expansion-panels v-model="openSectionInternal">
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(0)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(0)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Top Row Options</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div><strong>The following options will be available to you:</strong></div>
          <ul>
            <li><strong>Select View: Expanded/Collapsed</strong> - This will allow you to select whether you wish a Collapsed overview showing each GL Group summarized, or if you wish to view the details with an Expanded view</li>
            <li><strong>Hide Zero Only Rows Yes/No</strong> - This will give you the option to hide any row that has zeros for all columns. This means there must be zeros for the budget as well as for the prior year actuals.</li>
            <li><strong>Add Columns: Months</strong> - Checking this box will result in the twelve month's of your budget displaying.</li>
            <li><strong>Add Columns: Calculations</strong> - Checking this box will add a column displaying the two most recent calculations that might have been created for each GL Account</li>
            <li><strong>Add Columns: Assumptions</strong> - Checking this box will add a column displaying the Assumption statement. Clicking on the statement will bring up the Calculation/Assumption popup box which will display the two most recent calculations as well as allow you to view and/or edit the Assumption statement.</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(1)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(1)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Column Calculations</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div><strong>Per Dollar Section:</strong></div>
          <ul>
            <li><strong>2022 Budget</strong> - this is the sum of the budget's 12 months columns.</li>
            <li><strong>2021 Actuals</strong> - this is the current year's actual numbers up to the month selected in Settings > General Settings "Estimate Future Historicals." After that month, it will use the choice made on the Settings > Estimate Future Historicals drop-down box for the rest of the months.</li>
            <li><strong>Var $ -</strong></li>
            <li class="no-bullet">
              <ul>
                <li>The math for the Income section will be 2022 Budget - 2021 Actuals.</li>
                <li>The math for the Expense, Non-Operating Income, and Other Accounts will be based on the core setting: Show over budget expense accounts as a negative variance.</li>
                <li class="no-bullet">
                  <ul>
                    <li>If the box is not checked, the math will be the same as Income.</li>
                    <li>If the box is checked, the match will be 2021 Actuals - 2022 Budget.</li>
                  </ul>
                </li>
              </ul>
            </li>
            <li><strong>Var %</strong> - The math is Variance $ divided by 2022 Budget. Displayed as a percentage with 1 decimal place.</li>
          </ul>
          <div><strong>Per Unit Section:</strong></div>
          <ul>
            <li><strong>2022 Budget</strong> - 2022 Budget per dollar divided by the Total Units on the property. The total units can be verified by looking at a Rent Roll Summary, Property Occupancy Totals and looking at the total of the units.</li>
            <li><strong>2021 Actuals</strong> - 2022 Actuals per dollar divided by the Total Units</li>
            <li><strong>Var $ -</strong></li>
            <li class="no-bullet">
              <ul>
                <li>The math for the Income section will be 2022 Budget - 2021 Actuals.</li>
                <li>The math for the Expense, Non-Operating Income, and Other Accounts will be based on the core setting: Show over budget expense accounts as a negative variance.</li>
                <li class="no-bullet">
                  <ul>
                    <li>If the box is not checked, the math will be the same as Income.</li>
                    <li>If the box is checked, the match will be 2021 Actuals - 2022 Budget.</li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
          <div><strong>Per Square Footage Section:</strong></div>
          <ul>
            <li><strong>2022 Budget</strong> - 2022 Budget per dollar divided by the Total Square Footage on the property. The total Square Footage can be verified by looking at a Rent Roll Summary, Property Occupancy Totals and looking at the total of the square footage.</li>
            <li><strong>2021 Actuals</strong> - 2021 Actuals by dollar divided by the Total Square Footage</li>
            <li><strong>Var $ -</strong></li>
            <li class="no-bullet">
              <ul>
                <li>The math for the Income section will be 2022 Budget - 2021 Actuals.</li>
                <li>The math for the Expense, Non-Operating Income, and Other Accounts will be based on the core setting: Show over budget expense accounts as a negative variance.</li>
                <li class="no-bullet">
                  <ul>
                    <li>If the box is not checked, the math will be the same as Income.</li>
                    <li>If the box is checked, the match will be 2021 Actuals - 2022 Budget.</li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(2)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(2)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>GL Accounts</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>If there is an unresolved Flag on a GL Account, there will be an icon next to the GL account and if the page is loaded with Select View: Collapsed then there will be an icon next to the GL Group name. The icon on the specific GL Account line can be clicked to bring up the Comments/Flags popup.</div>
          <div>If the Add Columns: Assumptions is checked and you click on the Assumption statement or on the blank space where it would be, it will bring up the Calculations/Assumptions popup. This is the same popup with the same functionality that is accessed on the GL Account page.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Prefer to watch a video? <a @click="openExternalTopic('BudgetsPro Working Draft')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Want to see all BudgetsPro help articles? <a @click="openExternalTopic('BudgetsPro')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import HelpMixin from '@/mixins/Help.js'
  export default {
    name: 'WorkingDraftHelp',
    mixins: [HelpMixin]
  }
</script>

<style lang="scss" scoped>
  .no-bullet {
    list-style-type: none;
  }
  .icon {
      order: 0;
  }
  .header {
      order: 1;
  }
  img {
    max-width: 650px;
  }
  img.small {
    width: 35px;
  }
  .help-container {
      border-bottom: 1px solid var(--v-resGrey-lighten1)
  }
  .help-content div,
  .help-content li {
      margin-top:10px !important;
      margin-bottom:10px !important;
  }
  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }
  .pointer {
      cursor: pointer !important;
  }
  .separator {
    display: flex;
    align-items: center;
    text-align: center;
  }
  .separator::before,
  .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--v-resGrey-darken1);
  }

  .separator:not(:empty)::before {
    margin-right: .25em;
  }

.separator:not(:empty)::after {
  margin-left: .25em;
}
</style>
